import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Breadcrumbs,
  Link,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const TermsPage: React.FC = () => {
  const lastUpdated = 'May 15, 2023';

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 6, pt: 4 }}>
        {/* Header */}
        <LandingHeader />
        
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Terms of Service
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9, mb: 2 }}>
            Last Updated: {lastUpdated}
          </Typography>
          
          <Breadcrumbs aria-label="breadcrumb" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <Link color="inherit" href="/">
              Home
            </Link>
            <Typography color="rgba(255,255,255,0.9)">Terms of Service</Typography>
          </Breadcrumbs>
        </Container>
      </Box>
      
      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
          <Typography variant="body1" paragraph>
            Welcome to Contract AI. These Terms of Service ("Terms") govern your access to and use of the Contract AI platform and services (the "Services"). Please read these Terms carefully before using our Services. By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing or using our Services, you agree to these Terms. If you are using our Services on behalf of an organization (such as your employer), you are agreeing to these Terms on behalf of that organization, and you represent and warrant that you have the authority to do so. In such case, "you" and "your" will refer to that organization.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            2. Changes to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We may modify these Terms at any time, in our sole discretion. If we do so, we will let you know either by posting the modified Terms on our website or through other communications. It's important that you review the Terms whenever we modify them because if you continue to use the Services after we have posted modified Terms, you are indicating to us that you agree to be bound by the modified Terms. If you don't agree to be bound by the modified Terms, then you may not use the Services anymore.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            3. Services and Account Registration
          </Typography>
          <Typography variant="body1" paragraph>
            To access and use certain features of our Services, you may be required to register for an account. When you register for an account, you agree to provide accurate, current, and complete information about yourself and to update such information as necessary. You are responsible for safeguarding your account credentials and for any activity that occurs under your account. You agree to notify us immediately of any unauthorized access to or use of your account.
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to disable any user account, at any time, in our sole discretion for any or no reason, including if, in our opinion, you have violated any provision of these Terms.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            4. Subscription and Payment
          </Typography>
          <Typography variant="body1" paragraph>
            Some of our Services are provided on a subscription basis. You agree to pay all fees associated with your subscription plan. We may change our subscription fees at any time, but fee changes will not apply retroactively. If we change our fees, we will provide notice of the change on our website or by email, at least 30 days before the change is to take effect.
          </Typography>
          <Typography variant="body1" paragraph>
            Unless otherwise stated, all fees are quoted in U.S. Dollars. You are responsible for paying all taxes associated with your use of the Services, except for taxes based on our net income.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            5. User Content
          </Typography>
          <Typography variant="body1" paragraph>
            Our Services allow you to upload, submit, store, send, or receive content, including documents, contracts, notes, and other materials (collectively, "User Content"). You retain all rights in, and are solely responsible for, the User Content you upload, submit, store, send, or receive through our Services.
          </Typography>
          <Typography variant="body1" paragraph>
            By uploading, submitting, storing, sending, or receiving User Content through our Services, you grant us a worldwide, non-exclusive, royalty-free license to use, host, store, reproduce, modify, create derivative works, communicate, publish, publicly perform, publicly display, and distribute such User Content for the limited purpose of providing the Services to you and as otherwise permitted by our Privacy Policy.
          </Typography>
          <Typography variant="body1" paragraph>
            You represent and warrant that:
          </Typography>
          <List sx={{ pl: 4 }}>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="You own or have the necessary rights to use and authorize the use of all User Content as described in these Terms" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Your User Content does not violate any third party's intellectual property rights, privacy rights, publicity rights, or other rights" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Your User Content complies with these Terms and all applicable laws and regulations" />
            </ListItem>
          </List>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            6. Intellectual Property Rights
          </Typography>
          <Typography variant="body1" paragraph>
            The Services and their entire contents, features, and functionality (including but not limited to all information, software, text, displays, images, video, and audio, and the design, selection, and arrangement thereof), are owned by Contract AI, its licensors, or other providers of such material and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms permit you to use the Services for your personal, non-commercial use, or legitimate business purposes related to your role as a current or prospective customer of Contract AI. You must not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Services, except as follows:
          </Typography>
          <List sx={{ pl: 4 }}>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Your computer may temporarily store copies of such materials in RAM incidental to your accessing and viewing those materials" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="You may store files that are automatically cached by your Web browser for display enhancement purposes" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="You may print or download one copy of a reasonable number of pages of the Services for your own personal, non-commercial use and not for further reproduction, publication, or distribution" />
            </ListItem>
          </List>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            7. Prohibited Uses
          </Typography>
          <Typography variant="body1" paragraph>
            You may use the Services only for lawful purposes and in accordance with these Terms. You agree not to use the Services:
          </Typography>
          <List sx={{ pl: 4 }}>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="In any way that violates any applicable federal, state, local, or international law or regulation" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="For the purpose of exploiting, harming, or attempting to exploit or harm minors in any way" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="To transmit, or procure the sending of, any advertising or promotional material, including any 'junk mail,' 'chain letter,' 'spam,' or any other similar solicitation" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="To impersonate or attempt to impersonate Contract AI, a Contract AI employee, another user, or any other person or entity" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Services, or which, as determined by us, may harm Contract AI or users of the Services or expose them to liability" />
            </ListItem>
          </List>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            8. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL CONTRACT AI, ITS AFFILIATES, DIRECTORS, EMPLOYEES, AGENTS, SUPPLIERS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (A) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICES; (B) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICES; (C) ANY CONTENT OBTAINED FROM THE SERVICES; AND (D) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY, WHETHER OR NOT WE HAVE BEEN INFORMED OF THE POSSIBILITY OF SUCH DAMAGE.
          </Typography>
          <Typography variant="body1" paragraph>
            THE AGGREGATE LIABILITY OF CONTRACT AI TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE USE OF OR INABILITY TO USE THE SERVICES OR OTHERWISE UNDER THESE TERMS, WHETHER IN CONTRACT, TORT, OR OTHERWISE, IS LIMITED TO THE GREATER OF (A) THE AMOUNTS YOU HAVE PAID TO CONTRACT AI FOR USE OF THE SERVICES IN THE 12 MONTHS PRIOR TO THE EVENT GIVING RISE TO THE LIABILITY OR (B) $100.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            9. Indemnification
          </Typography>
          <Typography variant="body1" paragraph>
            You agree to defend, indemnify, and hold harmless Contract AI, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Services, including, but not limited to, your User Content, any use of the Services' content, services, and products other than as expressly authorized in these Terms, or your use of any information obtained from the Services.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            10. Termination
          </Typography>
          <Typography variant="body1" paragraph>
            We may terminate or suspend your access to all or part of the Services, without notice, for any conduct that we, in our sole discretion, believe is in violation of any applicable law, is in violation of these Terms, or is harmful to the interests of another user, a third-party provider, a service provider, or us. Upon termination, your right to use the Services will immediately cease.
          </Typography>
          <Typography variant="body1" paragraph>
            All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            11. Governing Law and Jurisdiction
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms and any dispute or claim arising out of or in connection with them or their subject matter or formation (including non-contractual disputes or claims) shall be governed by and construed in accordance with the laws of the State of California, without giving effect to any choice or conflict of law provision or rule.
          </Typography>
          <Typography variant="body1" paragraph>
            Any legal suit, action, or proceeding arising out of, or related to, these Terms or the Services shall be instituted exclusively in the federal courts of the United States or the courts of the State of California, in each case located in the City of San Francisco and County of San Francisco, although we retain the right to bring any suit, action, or proceeding against you for breach of these Terms in your country of residence or any other relevant country. You waive any and all objections to the exercise of jurisdiction over you by such courts and to venue in such courts.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            12. Contact Information
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about these Terms, please contact us at:
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Contract AI, Inc.<br />
            123 Market Street, Suite 400<br />
            San Francisco, CA 94105<br />
            United States<br />
            Email: legal@contractai.com<br />
            Phone: +1 (800) 555-0123
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Box sx={{ bgcolor: '#f8faff', p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Effective Date
            </Typography>
            <Typography variant="body2">
              These Terms of Service are effective as of {lastUpdated}.
            </Typography>
          </Box>
        </Paper>
      </Container>
      
      <Footer />
    </Box>
  );
};

export default TermsPage; 